import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import {
  horizontalCap,
  orientedFace,
  prismSolid,
  pyramidOverRing,
  ringPoints,
  triangulateRing,
} from '../solids';
import { buildVessel } from './churchVessel';
import { pointedWindows, rectWall } from './masonry';
import { buildConventRange } from './conventRange';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import type { LandmarkContext } from './landmarkFeature';
import {
  prinsenhofEntryHeight,
  prinsenhofFloors,
  prinsenhofFrame,
  prinsenhofPartFrame,
  prinsenhofPlan,
} from './prinsenhofConventsPlan';
import { layoutToWorld } from '../../../core/coords';

/** Individual source-read bodies of Cecilia and Catharina, on separate ground reservations. */
export function buildPrinsenhofConvent(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = prinsenhofFrame(landmark),
    reading = prinsenhofPlan(landmark),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = reading.chapel,
    chapelFrame = meshFrameOf(prinsenhofPartFrame(plan, chapel));
  buildVessel(
    builder,
    brick,
    chapelFrame,
    {
      u0: -chapel.length / 2,
      chordU: chapel.length / 2,
      vc: 0,
      half: chapel.width / 2,
      eaveY: chapel.eaves,
      ridgeY: chapel.ridge,
      apse: 'flat',
    },
    {
      longWindows: { sill: 2.1, top: chapel.eaves - 1.1 },
      westWindow: true,
      bay: chapel.length / chapel.bays,
    }
  );
  const bell = reading.belfry,
    bellRing = ringPoints([bell.u, 0], bell.radius, 8, Math.PI / 8);
  bellRing.forEach((from, i) => {
    const to = bellRing[(i + 1) % bellRing.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      chapelFrame,
      [from, to],
      [bell.foot, bell.eaves],
      pointedWindows(width, width, 0.5, bell.eaves - bell.foot - 0.2),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, chapelFrame, bellRing, bell.foot, false);
  pyramidOverRing(builder, brick, chapelFrame, bellRing, bell.eaves, {
    plan: [bell.u, 0],
    y: bell.tip,
  });
  for (const part of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(prinsenhofPartFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );

  const floors = prinsenhofFloors(landmark),
    base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    worldFrame: Frame = { origin: [0, 0, 0], yaw: 0 },
    garden = {
      ...brick.wall,
      color: colors.garden,
      surface: SURFACE.garden,
      pattern: PATTERN.none,
    },
    ground = {
      ...brick.wall,
      color: colors.ground,
      surface: SURFACE.street,
      pattern: PATTERN.none,
    };
  // Close the level court down to terrain; a shallow cap must not float above a lower bank. The
  // beds stand on the court's slab, not on their own feet beside its bottom.
  prismSolid(
    builder,
    garden,
    worldFrame,
    floors.court.ring,
    Math.min(...floors.court.ring.map(terrain)) - base - 0.05,
    floors.court.height,
    { top: true, bottom: false }
  );
  for (const bed of floors.beds)
    prismSolid(builder, garden, worldFrame, bed.ring, floors.court.height - 0.02, bed.height, {
      top: true,
      bottom: false,
    });
  const entryFrame = prinsenhofPartFrame(plan, floors.entry.part);
  const entryY = (p: readonly [number, number]) =>
    prinsenhofEntryHeight(entryFrame, floors.entry.part, p, terrain, base) - base;
  for (const tri of triangulateRing(floors.entry.ring))
    orientedFace(
      builder,
      ground,
      worldFrame,
      tri.map((p) => layoutToWorld(p, entryY(p))),
      [0, 1, 0]
    );
  const bottom = Math.min(...floors.entry.ring.map(terrain)) - base - 0.05;
  floors.entry.ring.forEach((from, i) => {
    const to = floors.entry.ring[(i + 1) % floors.entry.ring.length]!;
    orientedFace(
      builder,
      ground,
      worldFrame,
      [
        layoutToWorld(from, bottom),
        layoutToWorld(to, bottom),
        layoutToWorld(to, entryY(to)),
        layoutToWorld(from, entryY(from)),
      ],
      [to[1] - from[1], 0, to[0] - from[0]]
    );
  });
  horizontalCap(builder, ground, worldFrame, floors.entry.ring, bottom, false);
}
